import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth/useAuth";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";
import { getOrgDomain } from "@/lib/powtoon-oauth";
import {
  setAuthIntent,
  readAuthIntent,
  clearAuthIntent,
  clearAuthInflight,
} from "@/lib/auth/postLoginDestination";
import { AppHeader } from "@/components/layout/AppHeader";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export function LandingPage() {
  useDocumentMeta({
    title: "Powtoon Live Avatar — Interactive AI Training",
    description:
      "Create AI-powered learning experiences with live interactive avatars. Train, challenge, quiz, or present — all powered by Powtoon.",
  });

  const { user, isLoading, hasPermission } = useAuth();
  const { signInWithPowtoon, signOut, orgDomain, isAuthenticated } =
    usePowtoonAuth();
  const navigate = useNavigate();

  const urlParams = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search);
  }, []);
  const urlProjectId = urlParams?.get("projectId") ?? null;
  const urlOrgDomain = urlParams?.get("orgDomain") ?? null;

  const [tenantSwitching, setTenantSwitching] = useState(false);

  useEffect(() => {
    if (urlProjectId) {
      setAuthIntent({ kind: "learner", projectId: urlProjectId });
    } else if (urlOrgDomain) {
      setAuthIntent({ kind: "trainer" });
    }
  }, [urlProjectId, urlOrgDomain]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !urlOrgDomain) return;
    const stored = getOrgDomain();
    if (stored && stored !== urlOrgDomain) {
      setTenantSwitching(true);
      signOut();
      signInWithPowtoon();
    }
  }, [isLoading, isAuthenticated, urlOrgDomain, signOut, signInWithPowtoon]);

  useEffect(() => {
    if (isLoading || tenantSwitching) return;
    if (!isAuthenticated) return;
    if (hasPermission("live-avatars.train")) {
      clearAuthIntent();
      clearAuthInflight();
      navigate("/trainer", { replace: true });
      return;
    }
    const intent = readAuthIntent();
    if (intent?.kind === "learner") {
      clearAuthIntent();
      clearAuthInflight();
      navigate(`/learn/${intent.projectId}`, { replace: true });
    }
  }, [isLoading, isAuthenticated, tenantSwitching, hasPermission, navigate]);

  const isDeepLinkLearner = !!urlProjectId;
  const showLoader =
    isLoading ||
    tenantSwitching ||
    (isAuthenticated && isDeepLinkLearner) ||
    (isDeepLinkLearner && !isAuthenticated && !!urlOrgDomain);

  if (showLoader) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          {isDeepLinkLearner ? "Opening your training session…" : "Loading…"}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader>
        <button
          onClick={signInWithPowtoon}
          disabled={!orgDomain}
          className="rounded-full gradient-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Sign in with Powtoon
        </button>
      </AppHeader>

      <section className="relative overflow-hidden gradient-hero">
        <div className="mx-auto flex max-w-6xl flex-col lg:flex-row items-center gap-12 px-6 py-16 lg:py-24">
          <div className="flex-1 space-y-6 text-center lg:text-left animate-slide-up">
            <span className="inline-block rounded-full border border-primary/30 bg-primary/10 px-5 py-1.5 text-sm font-semibold text-primary">
              LiveAvatar Trainer, Powered by Powtoon
            </span>
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              <span className="text-gradient">Interactive AI</span>
              <br />
              Training Sessions
            </h1>
            <p className="max-w-lg text-lg text-muted-foreground mx-auto lg:mx-0">
              Create AI-powered learning experiences with live interactive
              avatars. Train, challenge, quiz, or present — all powered by
              cutting-edge AI.
            </p>
            {user ? (
              <p className="text-sm text-muted-foreground">
                Signed in as <span className="font-medium text-foreground">{user.name}</span>.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                  <button
                    onClick={signInWithPowtoon}
                    disabled={!orgDomain}
                    className="inline-flex items-center gap-2 rounded-full gradient-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90 glow-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <GraduationCap className="h-4 w-4" />
                    Sign in with Powtoon
                  </button>
                </div>
                {orgDomain ? (
                  <p className="text-xs text-muted-foreground">
                    Connecting to{" "}
                    <span className="font-mono">{orgDomain}.powtoon.com</span>
                  </p>
                ) : (
                  <p className="text-xs text-destructive">
                    Open this app via your Powtoon workspace link (must include{" "}
                    <span className="font-mono">?orgDomain=…</span>).
                  </p>
                )}
              </>
            )}
          </div>

          <div className="flex-1 max-w-md w-full animate-slide-up">
            <div className="glass-card overflow-hidden p-8 glow-primary">
              <div className="aspect-square rounded-xl gradient-primary grid place-items-center">
                <div className="text-center text-primary-foreground space-y-3 animate-float">
                  <Sparkles className="h-12 w-12 mx-auto" />
                  <p className="text-lg font-semibold">Your Live Avatar</p>
                  <p className="text-xs opacity-80">
                    Sign in to launch a session
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
